// Frequently Asked Questions for AEO (Answer Engine Optimisation).
// Rendered as visible accordions AND exposed as FAQPage JSON-LD schema —
// Google's AI Overviews, ChatGPT and Perplexity disproportionately cite
// FAQPage-structured content. Each Q is phrased as a real customer query.

export const FAQS = {
  home: [
    {
      q: "What property compliance services does Oxford Environmental Consultants offer?",
      a: "OEC delivers a one-stop property compliance service across the UK: asbestos surveys (management, refurbishment and demolition surveys), fire risk assessments, fire door inspections, flat entrance door inspections, compartmentation and fire stopping surveys, passive fire protection surveys, emergency lighting testing, fire alarm servicing, legionella risk assessments, mould surveys, building safety consultancy and remedial works oversight — all under one ISO-aligned governance framework.",
    },
    {
      q: "Which areas of the UK does OEC cover?",
      a: "We have national UK coverage from our Basildon, Essex headquarters. Core coverage includes London, East London, North London, Oxford, Reading, Cambridge, Watford, St Albans, Stevenage, Bury St Edmunds, Ipswich, Leiston, Dartford, Medway and Maidstone — with regular projects throughout Kent, Suffolk, Cambridgeshire, Hertfordshire, Berkshire, Oxfordshire and Greater London.",
    },
    {
      q: "How quickly can OEC mobilise an asbestos survey or fire risk assessment?",
      a: "Standard appointments are typically available within 5–10 working days. For urgent mobilisations — short-notice asbestos refurbishment surveys, fire safety re-inspections ahead of regulator visits, or portfolio-wide re-assessments — we have delivered 320-unit re-inspections in five working days. Contact us via enquiries@oxford-ec.co.uk or 03456 868 868 for short-notice requirements.",
    },
    {
      q: "Are OEC surveyors qualified and accredited?",
      a: "Yes. Asbestos surveys are delivered by BOHS P402-qualified surveyors and UKATA-registered analysts (with P403 Fibre Counting / P404 Air Sampling & Clearance where required). Fire risk assessments are carried out under IFSM / IFE-recognised competency. We operate under ISO 9001 (Quality), SMAS Worksafe (Certified) and SSIP-registered governance, with UKAS-accredited laboratory partners for all bulk sample and air monitoring analysis.",
    },
    {
      q: "What is the difference between an asbestos management survey and a refurbishment survey?",
      a: "An asbestos management survey (HSG 264) is a non-intrusive inspection during normal occupation — it forms the basis of your Asbestos Register and Management Plan. A refurbishment and demolition (R&D) survey is fully intrusive, conducted before any building works to confirm no presumed or confirmed ACMs remain in the scope of works. Both are statutory requirements under the Control of Asbestos Regulations 2012 (CAR 2012).",
    },
  ],
  asbestos: [
    {
      q: "Do I legally need an asbestos survey?",
      a: "Yes, if you have any duty to maintain or repair non-domestic premises built before 2000. Regulation 4 of the Control of Asbestos Regulations 2012 (CAR 2012) places an explicit duty on the duty holder to determine whether asbestos-containing materials (ACMs) are present, record their location and condition, and produce a written Asbestos Management Plan. Breach is a criminal offence with unlimited fines.",
    },
    {
      q: "Which asbestos survey do I need — management, refurbishment or demolition?",
      a: "A management survey is your baseline non-intrusive inspection for occupied premises. A refurbishment survey is intrusive and is required before any works that could disturb ACMs. A demolition survey is the most thorough intrusive survey, required before any demolition. All three are defined in HSE's HSG 264.",
    },
    {
      q: "How much does an asbestos survey cost in Oxford, London or Essex?",
      a: "Cost depends on building size, complexity and survey type. A management survey for a small commercial unit typically starts from £350–£600; refurbishment surveys for blocks of flats or schools can range from £1,500 upwards depending on scale and access. Contact us for a fixed-price quote at enquiries@oxford-ec.co.uk.",
    },
    {
      q: "How long is an asbestos survey valid?",
      a: "An asbestos management plan should be reviewed annually and the Asbestos Register updated whenever materials are disturbed, removed or damaged. A new survey is not always required each year — but the management plan and register must remain current.",
    },
  ],
  "fire-safety": [
    {
      q: "Who is legally required to carry out a fire risk assessment?",
      a: "Every 'responsible person' for non-domestic premises and the common parts of multi-occupied residential buildings must arrange a suitable and sufficient fire risk assessment under Article 9 of the Regulatory Reform (Fire Safety) Order 2005, reinforced by the Fire Safety Act 2021 and the Fire Safety (England) Regulations 2022.",
    },
    {
      q: "How often should a fire risk assessment be reviewed?",
      a: "Fire risk assessments must be reviewed regularly and whenever there is a significant change to the premises, occupancy, processes or following a fire. Best practice in multi-occupied residential buildings is annually, with interim quarterly common-parts inspections.",
    },
    {
      q: "What is a flat entrance door inspection?",
      a: "Under the Fire Safety (England) Regulations 2022, the responsible person for a multi-occupied residential building over 11 metres must inspect flat entrance doors annually (with quarterly common-parts inspections), checking they are FD30S-compliant and meet self-closing, intumescent seal and gap-tolerance requirements.",
    },
    {
      q: "What is a compartmentation survey?",
      a: "A compartmentation survey verifies that the fire-resisting walls, floors and stopping intended to limit fire and smoke spread between compartments remain intact. In converted and older buildings, compartmentation is rarely as complete as drawings suggest — and an intrusive sample is now the only defensible position post-Grenfell.",
    },
    {
      q: "Do you carry out fire door inspections in Oxford and Essex?",
      a: "Yes — OEC delivers FDIS-aligned fire door inspections across Oxford, Essex, London, Kent, Suffolk and Hertfordshire, covering common-parts fire doors, flat entrance doors and cross-corridor doors in residential, commercial and education premises.",
    },
  ],
  "water-hygiene": [
    {
      q: "What is a legionella risk assessment?",
      a: "A legionella risk assessment is a suitable and sufficient assessment of the risk of legionella bacteria in water systems, required by the Health and Safety at Work etc. Act 1974 and detailed in HSE's Approved Code of Practice L8. It identifies all hot and cold water assets, evaluates control measures and produces a written scheme of control.",
    },
    {
      q: "How often must a legionella risk assessment be reviewed?",
      a: "L8 requires the assessment to be reviewed regularly and whenever there is reason to suspect it is no longer valid — typically every two years for most non-domestic premises, with annual reviews for higher-risk sites such as healthcare and care homes.",
    },
  ],
  "damp-and-mould": [
    {
      q: "What is Awaab's Law?",
      a: "Awaab's Law, introduced under the Social Housing (Regulation) Act 2023, requires social landlords to investigate reports of damp and mould within fixed timescales and complete remedial works without delay. It was named after Awaab Ishak, the two-year-old who died from prolonged exposure to mould in a Rochdale flat in 2020.",
    },
    {
      q: "What is an HHSRS damp and mould assessment?",
      a: "The Housing Health and Safety Rating System (HHSRS) is the statutory framework under the Housing Act 2004 used by local authorities and risk-aware landlords to assess hazards in residential property. A damp & mould HHSRS assessment scores the hazard on the official Category 1 and Category 2 scale and recommends prioritised remediation.",
    },
  ],
  coverage: [
    {
      q: "Does OEC cover London for asbestos and fire safety work?",
      a: "Yes — London is a core coverage area. We deliver asbestos surveys, fire risk assessments, fire door inspections, compartmentation surveys and the full property compliance suite across all London boroughs including East London and North London.",
    },
    {
      q: "Does OEC cover Oxford and surrounding Oxfordshire?",
      a: "Yes — Oxford is our namesake city. We have particularly strong delivery capability across Oxfordshire and the M40 / M4 corridor, covering Oxford, Reading, Watford, St Albans and beyond.",
    },
    {
      q: "Does OEC cover Essex and Kent?",
      a: "Yes — our Basildon, Essex headquarters means we deliver same-week mobilisation across Essex, Kent and Suffolk including Dartford, Medway, Maidstone, Ipswich, Leiston and Bury St Edmunds.",
    },
    {
      q: "Does OEC cover Cambridge and the East of England?",
      a: "Yes — Cambridge, Stevenage and Bury St Edmunds are all within our regular East of England coverage area, alongside Ipswich, Leiston and the wider Suffolk and Cambridgeshire region.",
    },
  ],
};

// Helper: build FAQPage JSON-LD for a given FAQ array.
export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (faqs || []).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
